import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { generateDeviceFingerprint, getDeviceName } from '../utils/deviceFingerprint';

interface TwoFactorAuthState {
  requiresVerification: boolean;
  email: string;
  pendingSession: any;
  deviceFingerprint?: string;
}

export function useTwoFactorAuth() {
  const [twoFactorState, setTwoFactorState] = useState<TwoFactorAuthState>({
    requiresVerification: false,
    email: '',
    pendingSession: null,
  });

  const checkTwoFactorEnabled = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('two_factor_enabled')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error checking 2FA status:', error);
        return false;
      }

      return data?.two_factor_enabled || false;
    } catch (err) {
      console.error('Error in checkTwoFactorEnabled:', err);
      return false;
    }
  }, []);

  const sendTwoFactorCode = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'No session found' };
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-2fa-code`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ip_address: null
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, error: errorData.error || 'Failed to send code' };
      }

      return { success: true };
    } catch (err) {
      console.error('Error sending 2FA code:', err);
      return { success: false, error: 'Failed to send verification code' };
    }
  }, []);

  const verifyTwoFactorCode = useCallback(async (code: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'User not found' };
      }

      const { data, error } = await supabase.rpc('verify_2fa_code', {
        p_user_id: user.id,
        p_code: code
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data) {
        return { success: true };
      } else {
        return { success: false, error: 'Invalid code' };
      }
    } catch (err) {
      console.error('Error verifying 2FA code:', err);
      return { success: false, error: 'Failed to verify code' };
    }
  }, []);

  const initiateTwoFactor = useCallback((email: string, session: any) => {
    setTwoFactorState({
      requiresVerification: true,
      email,
      pendingSession: session,
    });
  }, []);

  const resetTwoFactor = useCallback(() => {
    setTwoFactorState({
      requiresVerification: false,
      email: '',
      pendingSession: null,
    });
  }, []);

  const checkDeviceIsTrusted = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const deviceFingerprint = generateDeviceFingerprint();

      const { data, error } = await supabase.rpc('verify_trusted_device', {
        p_user_id: userId,
        p_device_fingerprint: deviceFingerprint,
        p_user_agent: navigator.userAgent
      });

      if (error) {
        console.error('Error checking trusted device:', error);
        return false;
      }

      return data?.is_trusted || false;
    } catch (err) {
      console.error('Error in checkDeviceIsTrusted:', err);
      return false;
    }
  }, []);

  const registerTrustedDevice = useCallback(async (trustDurationDays: number = 30): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'User not found' };
      }

      const deviceFingerprint = generateDeviceFingerprint();
      const deviceName = getDeviceName();

      const { data, error } = await supabase.rpc('register_trusted_device', {
        p_user_id: user.id,
        p_device_fingerprint: deviceFingerprint,
        p_device_name: deviceName,
        p_user_agent: navigator.userAgent,
        p_trust_duration_days: trustDurationDays
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: data?.success || false };
    } catch (err) {
      console.error('Error registering trusted device:', err);
      return { success: false, error: 'Failed to register device' };
    }
  }, []);

  return {
    twoFactorState,
    checkTwoFactorEnabled,
    sendTwoFactorCode,
    verifyTwoFactorCode,
    initiateTwoFactor,
    resetTwoFactor,
    checkDeviceIsTrusted,
    registerTrustedDevice,
  };
}
